import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { customHeaders } from 'api/api'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { RestApi } from '@node-space/rest-api'
import { Box as Container } from '@node-space/storybook-components'
import { Callout } from '@node-space/storybook-components/dist/Callout'
import { Input } from '@node-space/storybook-components/dist/Input'
import { Input2FA } from '@node-space/storybook-components/dist/Input2FA'
import {
  AlignActions,
  Modal,
  ModalActions,
  ModalNavBar,
  ModalScrollable,
} from '@node-space/storybook-components/dist/Modal'
import { SuccessContainer } from '@node-space/storybook-components/dist/SuccessContainer'
import { Text } from '@node-space/storybook-components/dist/Text'
import { logError } from '@node-space/utils'
import LoadingAnimationDark from 'components/spinners/LoadingAnimationDark'
import UploadFile from 'components/UploadFile'
import { useNgBankAccountValidationMutation } from 'hooks/mutations/useNgBankAccountValidationMutation'
import { useBanksQuery } from 'hooks/queries/useBanks'
import {
  getBusinessData,
  getPaymentMethodTypes,
  getSouthAfricanBanks,
} from 'services/AccountService'
import { BankAccountError } from 'types/BankAccountsDetails'
import { BaseErrorResponse } from 'types/beneficiaries'
import { AddBankAcountFormPayload } from 'types/paymentMethod'
import { ErrorList } from 'types/payments'
import { CountryCodes, Currencies } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import {
  getBankAccountDataFactory,
  getFieldsToValidate,
  setBankAccountErrors,
} from 'utils/bankAccount'
import { Amp } from 'utils/tracker'
import { titleCase } from 'utils/utils'
import { addBankAccountDetailsSchema } from '../schemas/addBankAccountDetailsSchema'
import { EUR, GBP, NGN, ZAR } from './Currency'

interface AddBankAccountModalProps {
  selectedCurrency: string
  handleBackClick: () => void
  onSuccess: () => void
  onClose: () => void
}

const accountTypeOptions = [
  {
    label: 'Cheque',
    value: 'Cheque',
  },
  {
    label: 'Savings',
    value: 'Savings',
  },
  {
    label: 'Transmission',
    value: 'Transmission',
  },
]

export const AddBankAccountModal = ({
  selectedCurrency,
  handleBackClick,
  onSuccess,
  onClose,
}: AddBankAccountModalProps) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const formSchema = addBankAccountDetailsSchema(t)
  const {
    control,
    formState,
    register,
    setValue,
    resetField,
    trigger,
    clearErrors,
    watch,
    setError,
  } = useForm<AddBankAcountFormPayload>({
    resolver: yupResolver(formSchema),
  })

  const [accountHolderName] = useWatch({
    control,
    name: [
      'accountHolderName',
      'proofOfOwnershipDocument',
      'description',
      'currencyCode',
      // EUR
      'iban',
      'bic',
      // GBP
      'accountNumber',
      'sortCode',
      // ZAR
      'zarBank',
      'zarAccountNumber',
      'zarAccountType',
      'zarRoutingNumberBranchCode',
      // NGN
      'bankCode',
      'ngnAccountNumber',
    ],
    defaultValue: {
      accountHolderName: '',
      proofOfOwnershipDocument: null,
      description: '',
      currencyCode: selectedCurrency,
      // ERU
      iban: '',
      bic: '',
      // GBP
      accountNumber: '',
      sortCode: '',
      // ZAR
      zarBank: '',
      zarAccountNumber: '',
      zarAccountType: '',
      zarRoutingNumberBranchCode: '',
      // NGN
      bankCode: '',
      ngnAccountNumber: '',
    },
  })
  const formValues = watch()

  const [code, setCode] = useState('')
  const [show2FA, setShow2FA] = useState(false)
  const [twoFaError, setTwoFaError] = useState<ErrorList>(null)
  const [isGenericError, setIsGenericError] = useState(false)
  const [isLoading2FA, setIsLoading2FA] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [bankListOptions, setBankListOptions] = useState([])
  const [paymentMethodTypes, setPaymentMethodTypes] = useState([])
  const { bankList, isBankListError, isFetchingBankList } = useBanksQuery(
    CountryCodes.NG,
    Currencies.NGN,
    selectedCurrency === Currencies.NGN
  )
  const { mutate: validateNgBankAccount } = useNgBankAccountValidationMutation()

  const handleSubmit = async () => {
    const submit = async () => {
      const result = await validateAccountFields()
      if (result) {
        setShow2FA(true)
      }
    }

    if (selectedCurrency === Currencies.NGN) {
      setIsValidating(true)
      const isFormDetailsValid = await validateAccountFields()

      if (isFormDetailsValid) {
        validateNgBankAccount(
          {
            accountNumber: formValues?.ngnAccountNumber,
            bankCode: formValues?.bankCode,
          },
          {
            onSuccess: () => {
              setIsValidating(false)
              submit()
            },
            onError: (err: BaseErrorResponse) => {
              setBankAccountErrors(err as BankAccountError, selectedCurrency, t, setError)
              setIsValidating(false)
            },
          }
        )
      } else {
        setIsValidating(false)
      }
    } else {
      submit()
    }
  }

  const handleSelectChange = (value: string, field: keyof AddBankAcountFormPayload): void => {
    setValue(field, value)
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: keyof AddBankAcountFormPayload
  ) => {
    setValue(field, event?.target?.value)
  }

  const handleCodeChange = e => {
    setCode(e)
  }

  useEffect(() => {
    if (code.length === 6) {
      submitWithTwoFactor()
    }
  }, [code])

  const onResetClick = () => {
    setCode('')
  }

  const submitWithTwoFactor = async () => {
    setIsLoading2FA(true)

    const data = getBankAccountDataFactory(selectedCurrency, code, paymentMethodTypes, formValues)

    try {
      const response = await RestApi.post(true, 'payment-method', data, customHeaders)
      setIsLoading2FA(false)
      if (response.status === 201) {
        setShow2FA(false)
        setShowSuccess(true)
      }
    } catch (error) {
      logError('Error from AddBankAccountModal - submitWithTwoFactor', error)
      if (error.data?.errorList) {
        const otpError: ErrorList = error?.data?.errorList.find(error => error?.parameter === 'otp')

        if (otpError) {
          setTwoFaError(otpError)
        } else {
          setBankAccountErrors(error, selectedCurrency, t, setError)
          setShow2FA(false)
        }
      } else {
        setIsGenericError(true)
      }
      onResetClick()
      setIsLoading2FA(false)
    }
  }

  useEffect(() => {
    setValue('currencyCode', selectedCurrency)
    setFields()
  }, [selectedCurrency, bankList])

  useEffect(() => {
    if (!!showSuccess && selectedCurrency)
      Amp.track(AmplitudeEvent.MANAGE_ACCOUNT_BANK_ACCOUNT_ADDED, {
        category: AmplitudeEventCategory.APPLICATION,
        action: AmplitudeEventAction.CLICK,
        currency: selectedCurrency,
      })
  }, [selectedCurrency, showSuccess])

  const setFields = async () => {
    setIsLoading(true)
    setIsGenericError(false)

    try {
      // TODO: To be moved to React Query
      const paymentTypes = await getPaymentMethodTypes()
      if (paymentTypes?.length > 0) {
        setPaymentMethodTypes(paymentTypes)
      }

      if (selectedCurrency === Currencies.ZAR) {
        const southAfricanBanks = await getSouthAfricanBanks()
        if (southAfricanBanks?.length > 0) {
          const bankArray = southAfricanBanks?.map(bank => {
            return {
              label: bank.name,
              value: bank.id,
            }
          })
          setBankListOptions(bankArray)
        }
      }

      if (selectedCurrency === Currencies.NGN) {
        if (bankList?.banks?.length > 0) {
          const bankArray = bankList?.banks?.map(bank => {
            return {
              label: titleCase(bank?.bankName),
              value: bank?.bankIdentifier,
            }
          })
          setBankListOptions(bankArray)
        }
      }

      // TODO: To be moved to React Query
      const business = await getBusinessData()
      if (business?.registeredName) {
        setValue('accountHolderName', business?.registeredName)
      }
      setIsLoading(false)
    } catch (error) {
      logError('Error from AddBankAccountModal - setFields', error)
      setIsLoading(false)
    }
  }

  const determineFormFields = () => {
    switch (selectedCurrency) {
      case Currencies.EUR:
        return (
          <EUR
            errors={formState?.errors}
            formValues={formValues}
            handleChange={handleChange}
            register={register}
          />
        )
      case Currencies.GBP:
        return (
          <GBP
            errors={formState?.errors}
            formValues={formValues}
            handleChange={handleChange}
            register={register}
          />
        )
      case Currencies.ZAR:
        return (
          <ZAR
            errors={formState?.errors}
            formValues={formValues}
            bankOptions={bankListOptions}
            accountTypeOptions={accountTypeOptions}
            handleSelectChange={handleSelectChange}
            handleChange={handleChange}
            register={register}
          />
        )
      case Currencies.NGN:
        return (
          <NGN
            errors={formState?.errors}
            formValues={formValues}
            bankListOptions={bankListOptions}
            handleSelectChange={handleSelectChange}
            handleChange={handleChange}
            register={register}
          />
        )
      default:
        return
    }
  }

  const resetForm = () => {
    Object.keys(formValues || {})?.forEach(field => {
      resetField(field as keyof AddBankAcountFormPayload)
      if (field === 'proofOfOwnershipDocument') {
        setValue(field as keyof AddBankAcountFormPayload, null)
      } else {
        setValue(field as keyof AddBankAcountFormPayload, '')
      }
    })
    clearErrors()
  }

  const resetModal = () => {
    resetForm()
    setShow2FA(false)
    setShowSuccess(false)
  }

  const handleClose = () => {
    resetModal()
    onClose()
  }

  const currencySpecificFormFields = determineFormFields()

  //Pre submit validate (to display API errors like duplicate account name etc)
  const validateAccountFields = async () => {
    setIsLoading(true)
    clearErrors()
    const fieldsToValidate = getFieldsToValidate(selectedCurrency as Currencies)
    const isValidFormDetails = await trigger(fieldsToValidate)

    if (!isValidFormDetails) {
      setIsLoading(false)
      return false
    }

    const data = getBankAccountDataFactory(selectedCurrency, code, paymentMethodTypes, formValues)

    try {
      const response = await RestApi.post(true, 'payment-method/verify', data, customHeaders)
      if (response.status === 200) {
        setIsLoading(false)
        return true
      }
    } catch (error) {
      logError('Error from AddBankAccountModal - validateAccountFields', error)
      setIsLoading(false)
      setIsLoading2FA(false)
      if (error?.data?.errorList?.length) {
        setBankAccountErrors(error, selectedCurrency, t, setError)
        return false
      } else {
        setIsGenericError(true)
        return false
      }
    }
  }

  const onContinueClick = () => {
    resetModal()
    onSuccess()
  }

  return (
    <Modal closeOnBackgroundClick={false} visible={!!selectedCurrency} closeModal={handleClose}>
      {showSuccess ? (
        <>
          <SuccessContainer
            headline={t('bankAccount.addAccountSuccessMessageHeading')}
            description={t('bankAccount.addAccountSuccessMessageSubheading')}
          />
          <ModalActions
            actions={[
              {
                children: t('done'),
                onClick: onContinueClick,
              },
            ]}
            alignActions={AlignActions.CENTER}
          />
        </>
      ) : (
        <>
          <div className="min-w-full">
            {!!selectedCurrency && (
              <ModalNavBar
                title={t(`settingsBankAccountCurrencyHeading${selectedCurrency}`)}
                onClose={handleClose}
              />
            )}
          </div>
          {show2FA ? (
            <div className="p-6 w-full space-y-4">
              <Container flex direction="col" gapY={16}>
                <Text color="dark-grey" size="lg" weight="semibold" align="center">
                  {t('bankAccount.enterYour2FACode')}
                </Text>
                <Text tag="h1" color="disabled" size="sm" weight="normal" align="center">
                  {t('bankAccount.enterYour2FACodeDescription')}
                </Text>
              </Container>

              {isLoading2FA ? (
                <div className="flex justify-center py-4">{LoadingAnimationDark}</div>
              ) : (
                <>
                  {isGenericError && (
                    <Callout state="error" message={t('bankAccount.genericError')} />
                  )}
                  <Input2FA
                    onCompleteFill={() => null}
                    onChange={handleCodeChange}
                    error={!!twoFaError}
                    errorText={twoFaError?.message}
                    disabled={isLoading2FA}
                  />
                </>
              )}
            </div>
          ) : (
            <>
              <ModalScrollable>
                <div className="w-full p-6 space-y-8">
                  {(isGenericError || isBankListError) && (
                    <Callout state="error" message={t('bankAccount.genericError')} />
                  )}
                  <Input
                    {...register('accountHolderName')}
                    type="text"
                    inputMode="text"
                    label={t('accountHolderName')}
                    placeholder={t('accountHolderName')}
                    value={accountHolderName}
                    error={!!formState?.errors?.accountHolderName}
                    errorText={formState?.errors?.accountHolderName?.message}
                    onChange={e => handleChange(e, 'accountHolderName')}
                  />

                  {/* Dynamic Fields */}
                  {currencySpecificFormFields}

                  <div className="w-full mt-6">
                    <UploadFile
                      name="proofOfOwnershipFile"
                      title={t('proofOfBankOwnership')}
                      file={formValues?.proofOfOwnershipDocument}
                      setFile={fileData => setValue('proofOfOwnershipDocument', fileData)}
                      showIsRequired={!!formState?.errors?.proofOfOwnershipDocument}
                      tooltipContent={{
                        title: t('proofOfBankOwnershipTooltip'),
                      }}
                    ></UploadFile>
                  </div>
                </div>
              </ModalScrollable>
              <ModalActions
                actions={[
                  {
                    children: t('back'),
                    secondary: true,
                    onClick: () => {
                      handleBackClick()
                      resetModal()
                    },
                  },
                  {
                    children: t('settingsBankAccountAddButton'),
                    onClick: () => handleSubmit(),
                    loading: isLoading || isFetchingBankList || isValidating,
                  },
                ]}
              />
            </>
          )}
        </>
      )}
    </Modal>
  )
}
